import React, { useRef, useEffect, useState, forwardRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import brandbookService from '../services/brandbookApi';
import BrandbookHeader from '../components/BrandbookHeader';
import toast from 'react-hot-toast';

interface BrandValue {
  heading: string;
  subHeading: string;
  description: string;
}

interface Vision {
  oneLiner: string;
  description: string;
}

interface Voice {
  oneLiner: string;
  description: string;
}

interface Brandbook {
  id: number;
  company_name?: string;
  slogan: string;
  brand_values: BrandValue[];
  vision: Vision;
  voice: Voice;
  logo_url?: string;
  color_palette: string[];
  display_name?: string;
}

interface ApiResponse {
  data: Brandbook;
}

interface SectionHeaderProps {
  title: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title }) => (
  <div className="flex items-center justify-between mb-2">
    <span className="text-sm font-medium uppercase text-primary">{title}</span>
  </div>
);

interface SectionProps {
  title: string;
  children: React.ReactNode;
  className?: string;
  id: string;
}

const Section = forwardRef<HTMLDivElement, SectionProps>(
  ({ title, children, className = '', id }, ref) => (
    <div ref={ref} id={id} className={`mb-8 ${className}`}>
      <SectionHeader title={title} />
      <div className="rounded-lg text-text-primary">{children}</div>
    </div>
  )
);

Section.displayName = 'Section';

interface ColorBoxProps {
  color: string;
}

const ColorBox: React.FC<ColorBoxProps> = ({ color }) => (
  <div className="flex flex-col items-center">
    <div
      className="w-16 h-16 md:w-24 md:h-24 rounded-lg mb-2"
      style={{ backgroundColor: color }}
    />
    <span className="text-xs md:text-sm">{color}</span>
  </div>
);

interface OnThisPageProps {
  activeSection: string;
  className?: string;
}

const OnThisPage: React.FC<OnThisPageProps> = ({
  activeSection,
  className = '',
}) => {
  const navigationItems = [
    { label: 'Brand name', id: 'brand-name' },
    { label: 'Slogan', id: 'slogan' },
    { label: 'Brand values', id: 'brand-values' },
    { label: 'Brand vision', id: 'brand-vision' },
    { label: 'Brand voice', id: 'brand-voice' },
    { label: 'Brand logo', id: 'brand-logo' },
    { label: 'Colour Palette', id: 'colour-palette' },
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div
      className={`bg-background-main text-text-primary rounded-lg ${className}`}
    >
      {/* Desktop version */}
      <div className="hidden md:block p-6">
        <h2 className="text-center mb-8">ON THIS PAGE</h2>
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-secondary" />
          <nav className="relative">
            {navigationItems.map((item) => (
              <div key={item.id} className="relative group">
                <div
                  className={`absolute left-0 top-0 bottom-0 w-0.5 ${
                    activeSection === item.id
                      ? 'bg-primary'
                      : 'group-hover:bg-primary'
                  }`}
                />
                <div
                  onClick={() => scrollToSection(item.id)}
                  className={`pl-6 py-2 cursor-pointer transition-colors ${
                    activeSection === item.id
                      ? 'text-primary font-medium'
                      : 'hover:text-primary'
                  }`}
                >
                  {item.label}
                </div>
              </div>
            ))}
          </nav>
        </div>
      </div>

      {/* Mobile version */}
      <div className="scrollbar-hide md:hidden overflow-x-auto p-4">
        <div className="flex space-x-4 min-w-max">
          {navigationItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={` px-1 py-0 rounded-full text-sm whitespace-nowrap transition-colors
                ${
                  activeSection === item.id
                    ? 'text-primary'
                    : ' text-gray-600 hover:bg-gray-200'
                }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const ViewBrandbookPage: React.FC = () => {
  const { brandbook_id } = useParams<{ brandbook_id: string }>();
  const [activeSection, setActiveSection] = useState<string>('brand-name');
  const sectionRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

  const { data, isLoading, isError } = useQuery<ApiResponse, Error>({
    queryKey: ['brandbook', brandbook_id],
    queryFn: async () => {
      const response = await brandbookService.getBrandbookById(
        Number(brandbook_id)
      );
      return { data: response.data as unknown as Brandbook };
    },
    enabled: !!brandbook_id,
  });

  useEffect(() => {
    const options: IntersectionObserverInit = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.target.id) {
          setActiveSection(entry.target.id);
        }
      });
    }, options);

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      sectionRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  const setRef = (id: string) => (el: HTMLDivElement | null) => {
    if (el) {
      sectionRefs.current.set(id, el);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-lg">Loading...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex justify-center items-center min-h-screen text-red-600">
        <p className="text-lg">Error loading brandbook</p>
      </div>
    );
  }

  const brandbook = data?.data;

  if (!brandbook) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-lg">No brandbook data available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-background-main min-h-screen">
      <BrandbookHeader
        title={brandbook.display_name || 'Brand Name'}
        onExport={() => {
          toast.success('Content exported successfully');
        }}
        onSaveClick={() => {
          toast.success('Brandbook saved successfully');
        }}
        viewOnly={true}
      />

      <div className="flex-1 p-4 md:p-8">
        {/* Mobile Navigation - Fixed at top for mobile */}
        <div className="md:hidden mb-6 sticky top-0 z-10">
          <OnThisPage activeSection={activeSection} />
        </div>

        <div className="flex flex-col md:flex-row md:space-x-4">
          {/* Main Content */}
          <div className="w-full md:w-3/4">
            <div id="brand-name" ref={setRef('brand-name')} className="mb-8">
              <SectionHeader title="BRAND NAME" />
              <h1 className="text-2xl md:text-4xl text-text-primary font-bold bg-background p-4 md:p-6 rounded-lg">
                {brandbook.company_name || 'Company Name missing while view'}
              </h1>
            </div>

            <Section id="slogan" title="SLOGAN" ref={setRef('slogan')}>
              <h2 className="text-lg md:text-xl font-medium bg-background p-3">
                {brandbook.slogan}
              </h2>
            </Section>

            <Section
              id="brand-values"
              title="BRAND VALUES"
              ref={setRef('brand-values')}
            >
              <div className="space-y-4 md:space-y-6 p-3 bg-background">
                {brandbook.brand_values.map(
                  (value: BrandValue, index: number) => (
                    <div key={index} className="space-y-2">
                      <h3 className="font-bold text-base md:text-lg">
                        Value {index + 1}: {value.heading}
                      </h3>
                      <h4 className="font-medium text-sm md:text-base">
                        {value.subHeading}
                      </h4>
                      <p className="text-sm md:text-base">
                        {value.description}
                      </p>
                    </div>
                  )
                )}
              </div>
            </Section>

            <Section
              id="brand-vision"
              title="BRAND VISION"
              ref={setRef('brand-vision')}
            >
              <div className="space-y-4 bg-background p-3">
                <p className="font-medium text-sm md:text-base">
                  {brandbook.vision.oneLiner}
                </p>
                <p className="text-sm md:text-base">
                  {brandbook.vision.description}
                </p>
              </div>
            </Section>

            <Section
              id="brand-voice"
              title="BRAND VOICE"
              ref={setRef('brand-voice')}
            >
              <div className="space-y-4 bg-background p-3">
                <p className="font-medium text-sm md:text-base">
                  {brandbook.voice.oneLiner}
                </p>
                <p className="text-sm md:text-base">
                  {brandbook.voice.description}
                </p>
              </div>
            </Section>

            {brandbook.logo_url && (
              <Section id="brand-logo" title="LOGO" ref={setRef('brand-logo')}>
                <div className="bg-background rounded-lg p-4 md:p-8 flex items-center justify-center">
                  <img
                    src={brandbook.logo_url}
                    alt="Brand Logo"
                    className="max-w-full h-auto"
                  />
                </div>
              </Section>
            )}

            <Section
              id="colour-palette"
              title="COLOUR PALETTE"
              ref={setRef('colour-palette')}
            >
              <div className="grid grid-cols-2 md:flex md:justify-between gap-4">
                {brandbook.color_palette.map((color: string, index: number) => (
                  <ColorBox key={index} color={color} />
                ))}
              </div>
            </Section>
          </div>

          {/* Desktop Navigation - Fixed position with sticky top */}
          <aside className="hidden md:block md:w-1/4">
            <div className="sticky top-8">
              <OnThisPage activeSection={activeSection} />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ViewBrandbookPage;
